import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CustomInput from "../../../components/CustomInput";
import CustomDropdown from "../../../components/CustomDropdown";
import CustomButton from "../../../components/CustomButton";
import Sidebar from "../../../components/Sidebar";
import { clearHomePageInfosClientCache, getHomePageInfos, getCashInMethod, getTradingAccountsTrx, createCashInOperation, ensureLocation } from "../../../utils/apiClient";
import AppConstants from "../../../utils/appConstants";
import { TransactionType, FeesFor, TransactionStatus } from "../../../utils/enums";
import { checkMustSign, getTodayCts, checkCtsStatus, CtsStatus } from "../../../utils/ctsHelper";
import CtsCreationDialog from "../../../components/CtsCreationDialog";
import TransactionPasswordDialog from "../../../components/TransactionPasswordDialog";
import { getPermissionControllers, hasCustomerPermission, hasCustomerPermissionEither, hasBrokerPermission, isAccountApprovalLocked } from "../../../utils/permissionHelper";
import PermissionCustomerModelController from "../../../controller/model/PermissionCustomerModelController";
import PermissionBrokerModelController from "../../../controller/model/PermissionBrokerModelController";
import WalletController from "../../../controller/walletController";
import WalletCurrencyFlag from "../../../components/WalletCurrencyFlag";
import {
  formatCashWalletDropdownLabel,
  resolveCashWalletCurrencyDisplay,
} from "../../../utils/currencyFlagHelper";
import { useToast } from "../../../contexts/ToastContext";
import { openSupportChatOnCurrentPage } from "../../../utils/supportChatNavigation";
import ManualCorrectionWarning from "../../../components/ManualCorrectionWarning";
import { isSelectedMt5BalanceNegative } from "../../../utils/manualCorrection";
import useManualCorrectionEnabled from "../../../utils/useManualCorrectionEnabled";
import CashInRequestController from "../../../controller/Transaction/cashInRequestConrtoller";
import { markWhishReturnActivity, claimWhishReturnHandling } from "../../../utils/whishReturn";
import "../../css/cachIn.css";
import mt5Logo from "../../../assets/MT5.png";

// Helper function to extract value from JSON object or string
const extractValue = (value) => {
  if (!value) return "";
  
  // If it's already a string, check if it's a JSON string
  if (typeof value === "string") {
    // Check if it looks like a JSON object string
    const trimmed = value.trim();
    if ((trimmed.startsWith("{") && trimmed.endsWith("}")) || 
        (trimmed.startsWith("[") && trimmed.endsWith("]"))) {
      try {
        const parsed = JSON.parse(trimmed);
        if (typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)) {
          // Extract first value from object
          const values = Object.values(parsed);
          return values.length > 0 ? String(values[0]).trim() : "";
        }
        return trimmed;
      } catch (e) {
        // Not valid JSON, return as is
        return value;
      }
    }
    return value;
  }
  
  // If it's an object (not null)
  if (typeof value === "object" && value !== null) {
    // If it's an array, return first element
    if (Array.isArray(value)) {
      return value.length > 0 ? extractValue(value[0]) : "";
    }
    // If it's an object, extract the first value
    const values = Object.values(value);
    const extracted = values.length > 0 ? String(values[0]).trim() : "";
    return extracted;
  }
  
  return String(value).trim();
};

const isWhishMethod = (method) => {
  const r = (method?.RELATED_TO ?? method?.relatedTo ?? "").toString().toLowerCase();
  return r === "whish" || method?.ID === 2;
};

const getWalletCurrencyCode = (walletData) =>
  (resolveCashWalletCurrencyDisplay(walletData, "").code || "").toString().toUpperCase();

export default function CashIn() {
  const { t } = useTranslation();
  const { success: showSuccess, error: showError, warning: showWarning } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  // Default to MT5 tab (will be adjusted based on permissions)
  const [activeTab, setActiveTab] = useState("mt5"); // "mt5" or "wallet"
  
  // Form fields
  const [amount, setAmount] = useState("");
  const [account, setAccount] = useState(null); // For MT5 tab - dropdown
  const [wallet, setWallet] = useState(null); // For Wallet tab - dropdown
  const [paymentMethod, setPaymentMethod] = useState(null); // Dropdown
  
  // Wallets from API
  const [wallets, setWallets] = useState([]);
  
  const [user, setUser] = useState(null);
  const manualCorrectionEnabled = useManualCorrectionEnabled();
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [processing, setProcessing] = useState(false);
  const [locationChecking, setLocationChecking] = useState(false);
  const [showPasswordPopup, setShowPasswordPopup] = useState(false);
  const [passwordDialogData, setPasswordDialogData] = useState(null);
  const [activeProcessingType, setActiveProcessingType] = useState("bank");

  // Payment methods from API
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [paymentMethodsLoading, setPaymentMethodsLoading] = useState(true);
  const [selectedPaymentMethodData, setSelectedPaymentMethodData] = useState(null);
  
  // Trading accounts from API
  const [tradingAccounts, setTradingAccounts] = useState([]);
  
  // Document attachment
  const [documentFile, setDocumentFile] = useState(null);
  const [documentFileName, setDocumentFileName] = useState("");

  // CTS dialog state
  const [showCtsDialog, setShowCtsDialog] = useState(false);
  const [ctsInitialSourceOfFunds, setCtsInitialSourceOfFunds] = useState(null);
  const [whishWaiting, setWhishWaiting] = useState(false);
  const [whishRequestToken, setWhishRequestToken] = useState("");

  // Get permission controllers from user data
  const permissions = useMemo(() => getPermissionControllers(user), [user]);
  const {
    customerPerm,
    customerDevicePerm,
    brokerPerm,
    brokerDevicePerm,
  } = permissions;

  // Global approval/profile lock:
  // - Profile not completed OR KYC in "Account Approval Required" → fully locked
  const approvalLocked = useMemo(() => isAccountApprovalLocked(user), [user]);

  const canCashInWallet = !approvalLocked && (hasCustomerPermission(
    customerPerm,
    customerDevicePerm,
    PermissionCustomerModelController.PER__CAN_CASH_IN
  ) || hasBrokerPermission(
    brokerPerm,
    brokerDevicePerm,
    PermissionBrokerModelController.PER__CAN_COLLECT_CASHIN
  ) || hasBrokerPermission(
    brokerPerm,
    brokerDevicePerm,
    PermissionBrokerModelController.PER__CAN_TRANSFER_CASHIN
  ));

  // MT5 tab matches the app: USD -> MT5 is allowed when the account or the device has the flag.
  const canCashInMT5 = canCashInWallet &&
    hasCustomerPermissionEither(
      customerPerm,
      customerDevicePerm,
      PermissionCustomerModelController.PER__CAN_INTERNAL_TRANSFER_USD_MT5
    );

  // Auto-switch to available tab if current tab is locked
  // This ensures users can't stay on a locked tab
  useEffect(() => {
    // Only run this effect after user data is loaded (not during initial loading)
    if (loading || !user) {
      return;
    }
    
    // If MT5 tab is active but user doesn't have permission, switch to Wallet
    if (activeTab === "mt5" && !canCashInMT5) {
      if (canCashInWallet) {
        setActiveTab("wallet");
        setAmount(""); // Clear amount when auto-switching tabs
      } else {
        // Both tabs are locked - keep current but form will be disabled

      }
    } 
    // If Wallet tab is active but user doesn't have permission, switch to MT5
    else if (activeTab === "wallet" && !canCashInWallet) {
      if (canCashInMT5) {
        setActiveTab("mt5");
        setAmount(""); // Clear amount when auto-switching tabs
      } else {
        // Both tabs are locked - keep current but form will be disabled

      }
    }
  }, [activeTab, canCashInMT5, canCashInWallet, loading, user]);

  // Inputs stay editable whenever cash-in itself is allowed.
  // CAN_UPDATE_LEVERAGE only controls changing account leverage and must not freeze this form.
  const canProceed = canCashInWallet;

  const selectedWalletDataForMethodFilter = useMemo(() => {
    const selectedWalletValue = typeof wallet === "object" ? wallet?.value : wallet;
    return wallets.find((w) => w.value === selectedWalletValue)?.data ?? null;
  }, [wallet, wallets]);

  const filteredPaymentMethods = useMemo(() => {
    if (activeTab !== "wallet" || !selectedWalletDataForMethodFilter) {
      return paymentMethods;
    }

    const walletCurrencyCode = getWalletCurrencyCode(selectedWalletDataForMethodFilter);
    const canUseWhish = walletCurrencyCode === "USD" || walletCurrencyCode === "LBP";

    return paymentMethods.filter((method) => {
      const methodData = method?.data || null;
      if (!isWhishMethod(methodData)) {
        return true;
      }
      return canUseWhish;
    });
  }, [activeTab, paymentMethods, selectedWalletDataForMethodFilter]);


  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setErrors({});
        
        // Try to get user data from sessionStorage first (to avoid redundant API calls)
        let userDataFromStorage = null;
        try {
          const storedUserData = sessionStorage.getItem('userData');
          const storedTimestamp = sessionStorage.getItem('userDataTimestamp');
          
          // Use stored data if it exists and is less than 5 minutes old
          if (storedUserData && storedTimestamp) {
            const age = Date.now() - parseInt(storedTimestamp, 10);
            if (age < 5 * 60 * 1000) { // 5 minutes
              userDataFromStorage = JSON.parse(storedUserData);
              setUser(userDataFromStorage);

            }
          }
        } catch (storageError) {

        }
        
        // Refresh user status via API when needed; keep cache benefits by default.
        try {
          const userResponse = await getHomePageInfos();
          const userData = userResponse?.decryptedData || userResponse?.data;
          
          if (userData && userData.CODE === AppConstants.responseOk && userData.DATA) {
            setUser(userData.DATA);
            // Store in sessionStorage for future use
            try {
              sessionStorage.setItem('userData', JSON.stringify(userData.DATA));
              sessionStorage.setItem('userDataTimestamp', Date.now().toString());
            } catch (storageError) {

            }
          }
        } catch (_) {
          // Keep using cached user data when request fails.
        }

        // Fetch cash-in methods

        setPaymentMethodsLoading(true);
        try {
          const cashInMethodResponse = await getCashInMethod("en");
          const cashInMethodData = cashInMethodResponse?.decryptedData || cashInMethodResponse?.data;

          // Process payment methods from response
          if (cashInMethodData && cashInMethodData.CODE === AppConstants.responseOk && cashInMethodData.DATA) {
            const methods = Array.isArray(cashInMethodData.DATA) ? cashInMethodData.DATA : [];
            // Transform methods to dropdown options with METHOD_NAME as label
            const methodOptions = methods.map((method, index) => {
            // Extract METHOD_NAME - handle both string and object formats
            const rawMethodName = method.METHOD_NAME || method.NAME;

            // Use extractValue to get the clean value
            let methodName = extractValue(rawMethodName);
            
            // If extraction failed, try fallback methods
            if (!methodName || methodName === "" || methodName === "{}" || methodName === "null") {
              if (typeof rawMethodName === "string") {
                // Try regex extraction from JSON string
                const jsonMatch = rawMethodName.match(/"([^"]+)"/);
                if (jsonMatch && jsonMatch[1]) {
                  methodName = jsonMatch[1];
                } else {
                  methodName = rawMethodName.trim();
                }
              } else if (typeof rawMethodName === "object" && rawMethodName !== null) {
                // Direct object access
                const keys = Object.keys(rawMethodName);
                if (keys.length > 0) {
                  methodName = String(rawMethodName[keys[0]] || "").trim();
                }
              }
            }
            
            // Final cleanup - ensure we have a clean, non-empty string
            methodName = String(methodName || "").trim();
            if (!methodName || methodName === "{}" || methodName === "null" || methodName === "undefined" || methodName === "") {
              methodName = "Unknown Method";
            }

            return {
              value: method.METHOD_ID || method.ID || method.id,
              label: methodName, // Clean string label - MUST be a plain string
              data: method // Store full method data for later use
            };
          });
          
            // Verify all labels are strings
            methodOptions.forEach((opt, idx) => {
              if (typeof opt.label !== "string") {
                opt.label = String(opt.label || "Unknown Method");
              }
            });
            setPaymentMethods(methodOptions);
          } else {
            setPaymentMethods([]);
          }
        } finally {
          setPaymentMethodsLoading(false);
        }

        // Fetch trading accounts

        const tradingAccountsResponse = await getTradingAccountsTrx([0, 1], 0);
        const tradingAccountsData = tradingAccountsResponse?.decryptedData || tradingAccountsResponse?.data;
        
        // Process trading accounts from response
        if (tradingAccountsData && tradingAccountsData.CODE === AppConstants.responseOk && tradingAccountsData.DATA) {
          const accounts = Array.isArray(tradingAccountsData.DATA) ? tradingAccountsData.DATA : [];
          // Transform accounts to dropdown options
          // Format: TRADING_ID (AMOUNT CURRENCY_SYMBOL)
          const accountOptions = accounts.map((acc) => {
            // Get TRADING_ID from nested account object or direct property
            const tradingId = acc.account?.account_information?.TRADING_ID || acc.TRADING_ID || "";
            const amount = acc.AMOUNT || acc.CURRENT_BALENCE || acc.CURRENT_BALANCE || acc.BALANCE || 0;
            // Get CURRENCY_SYMBOL from account or nested currencies object
            const currencySymbol = acc.currencies?.CURRENCY_SYMBOL || 
                                   acc.CURRENCY_SYMBOL || 
                                   "";
            
            // Format: TRADING_ID (AMOUNT CURRENCY_SYMBOL)
            const displayText = `${tradingId} (${amount} ${currencySymbol})`;
            
            return {
              value: acc.ID ?? acc.id, // WALLET_ID for MT5: trading account top-level ID
              label: displayText,
              data: acc
            };
          });
          setTradingAccounts(accountOptions);

        }

        // E-wallets for Wallet tab: same source as API get-all-ewallets-info + get-all-ewallets-amount (not dashboard/MT5 merge).

        try {
          const ewData = await WalletController.fetchEWalletsWithAmounts();
          const latestWallets = Array.isArray(ewData?.wallets) ? ewData.wallets : [];

          if (latestWallets.length > 0) {
            // Transform wallets to dropdown options
            // Show CURRENCY_SYMBOL in the dropdown
            const walletOptions = latestWallets.map((wallet) => {
              const balance = wallet.CURRENT_BALENCE || wallet.CURRENT_BALANCE || wallet.BALANCE || wallet.AMOUNT || 0;
              const displayText = formatCashWalletDropdownLabel(wallet, balance);
              return {
                value: wallet.ID ?? wallet.id,
                label: displayText,
                data: wallet
              };
            });
            setWallets(walletOptions);

          } else {

            setWallets([]);
          }
        } catch (walletError) {

          setWallets([]);
        }
        
      } catch (error) {
        showError("Error Occurred", error.message || "Failed to load user information");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleProceed = async () => {
    const newErrors = {};
    setProcessing(true);
    setLocationChecking(true);
    setErrors((prev) => ({ ...prev, general: null }));

    const loc = await ensureLocation();
    setLocationChecking(false);
    if (!loc.ok) {
      const msg =
        loc.error === "location_denied"
          ? "Please enable location access to continue."
          : loc.error === "location_unavailable"
            ? "Location is unavailable. Please check your device settings."
            : "Location is required to continue. Please enable it.";
      setProcessing(false);
      showError("Action Required", msg);
      return;
    }

    // Validate inputs
    if (!amount || amount.trim() === "") {
      newErrors.amount = "Amount is required";
    } else {
      // Validate amount is a valid number
      const amountValue = parseFloat(amount.trim());
      if (isNaN(amountValue) || amountValue <= 0) {
        newErrors.amount = "Amount must be a valid positive number";
      } else {
        // Validate against minimum from RANGE or MIN
        // Check account's currencies.RANGE first, then payment method's RANGE/MIN
        // Amount must be GREATER THAN (not equal to) the minimum
        let selectedAccountData = null;
        let selectedWalletData = null;
        if (activeTab === "mt5" && account) {
          // Find the selected account data
          const selectedAccount = tradingAccounts.find(
            (acc) => acc.value === (typeof account === "object" ? account.value : account)
          );
          selectedAccountData = selectedAccount?.data || null;
        } else if (activeTab === "wallet" && wallet) {
          // Find the selected wallet data
          const selectedWallet = wallets.find(
            (w) => w.value === (typeof wallet === "object" ? wallet.value : wallet)
          );
          selectedWalletData = selectedWallet?.data || null;
        }
        
        const minValue = getMinValue(selectedPaymentMethodData, selectedAccountData || selectedWalletData, activeTab);
        const maxValue = getMaxValue(selectedPaymentMethodData, selectedAccountData);

        // Validate minimum
        if (minValue !== null && !isNaN(minValue)) {
          if (amountValue <= minValue) {
            // Get currency symbol from account or payment method
            const currencySymbol = selectedAccountData?.currencies?.CURRENCY_SYMBOL || 
                                 selectedPaymentMethodData?.CURRENCY_SYMBOL || "";
            newErrors.amount = `Amount must be greater than ${minValue}${currencySymbol ? ` ${currencySymbol}` : ""}`;

          }
        }
        
        // Validate maximum
        if (maxValue !== null && !isNaN(maxValue)) {
          if (amountValue > maxValue) {
            // Get currency symbol from account or payment method
            const currencySymbol = selectedAccountData?.currencies?.CURRENCY_SYMBOL || 
                                 selectedPaymentMethodData?.CURRENCY_SYMBOL || "";
            newErrors.amount = `Max is ${maxValue.toLocaleString()}${currencySymbol ? ` ${currencySymbol}` : ""}`;

          }
        }
        
        if (!newErrors.amount) {

        }
      }
    }
    
    if (activeTab === "mt5" && !account) {
      newErrors.account = "Account is required";
    }
    
    if (activeTab === "wallet" && !wallet) {
      newErrors.wallet = "Wallet is required";
    }

    if (activeTab === "wallet" && selectedPaymentMethodData && isWhishMethod(selectedPaymentMethodData)) {
      const selectedWalletValue = typeof wallet === "object" ? wallet?.value : wallet;
      const selectedWalletData = wallets.find((w) => w.value === selectedWalletValue)?.data ?? null;
      const walletCurrencyCode = getWalletCurrencyCode(selectedWalletData);
      if (walletCurrencyCode !== "USD" && walletCurrencyCode !== "LBP") {
        newErrors.paymentMethod = "Whish supports only USD or LBP wallets.";
      }
    }
    
    if (!paymentMethod) {
      newErrors.paymentMethod = "Payment Method is required";
    }
    
    // Validate document if required
    if (selectedPaymentMethodData?.REQUIRED_DOCUMENT === 1 && !documentFile) {
      newErrors.document = "Document attachment is required";
    }
    
    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {

      setProcessing(false);
      return;
    }

    const amountValue = parseFloat(amount.trim().replace(/,/g, ""));
    const walletId = activeTab === "mt5"
      ? (typeof account === "object" ? account.value : account)
      : (typeof wallet === "object" ? wallet.value : wallet);
    let currencyId = null;
    if (activeTab === "mt5" && account) {
      const sel = tradingAccounts.find((a) => a.value === (typeof account === "object" ? account.value : account));
      const d = sel?.data;
      currencyId = d?.currencies?.ID ?? d?.currencies?.CURRENCY_ID ?? d?.CURRENCY_ID ?? d?.CURRENCY;
    } else if (activeTab === "wallet" && wallet) {
      const sel = wallets.find((w) => w.value === (typeof wallet === "object" ? wallet.value : wallet));
      const d = sel?.data;
      currencyId = d?.currencies?.ID ?? d?.currencies?.CURRENCY_ID ?? d?.CURRENCY_ID ?? d?.CURRENCY;
    }
    const cid = currencyId != null ? parseInt(currencyId, 10) : null;

    // Get amount and currency ID for CTS check (matches Flutter lines 512-519)
    let ctsStatus = CtsStatus.SIGNED;
    
    if (cid != null) {
      const ctsCheckResult = await checkMustSign({
        amount: amountValue,
        currencyId: cid,
      });

      if (ctsCheckResult.mustSign && !ctsCheckResult.error) {
        ctsStatus = await checkCtsStatus();

        // Force CTS check if amount threshold requires it (matches Flutter lines 531-537)
        if (ctsCheckResult.ctsData == null || !ctsCheckResult.ctsData.isSigned) {
          ctsStatus = ctsCheckResult.ctsData == null
              ? CtsStatus.NEEDS_CREATION
              : CtsStatus.NEEDS_SIGNATURE;
        }
      }
      // If mustSign is false, ctsStatus remains SIGNED and we skip the dialog
    } else {
      // If no currencyId, check CTS status anyway (fallback behavior) (matches Flutter line 542)
      ctsStatus = await checkCtsStatus();
    }

    if (ctsStatus !== CtsStatus.SIGNED) {
      // Matches Flutter lines 545-588
      if (ctsStatus === CtsStatus.NEEDS_CREATION) {
        // Get default source of funds (matches Flutter lines 548-551)
        const ctsData = await getTodayCts();
        const defaultSourceOfFunds = ctsData?.sourceOfFunds ?? null;
        
        // Show CTS Creation Dialog (matches Flutter lines 553-562)
        setCtsInitialSourceOfFunds(defaultSourceOfFunds);
        setShowCtsDialog(true);
        setProcessing(false);
        return; // Wait for dialog completion via handleCtsSuccess callback
      } else if (ctsStatus === CtsStatus.NEEDS_SIGNATURE) {
        // Get CTS data and show Signature Dialog (matches Flutter lines 563-583)
        const ctsData = await getTodayCts();
        if (ctsData != null) {
          setCtsInitialSourceOfFunds(ctsData.sourceOfFunds);
          setShowCtsDialog(true);
          setProcessing(false);
          return; // Wait for dialog completion via handleCtsSuccess callback
        } else {
          // Error loading CTS (matches Flutter lines 575-581)
          setProcessing(false);
          return;
        }
      }
    }

    await submitCashInRequest();
  };

  const handleCtsSuccess = async () => {
    setShowCtsDialog(false);
    await submitCashInRequest();
  };

  const refreshLocalWallets = async () => {
    try {
      const ewData = await WalletController.fetchEWalletsWithAmounts();
      if (ewData && Array.isArray(ewData.wallets) && ewData.wallets.length > 0) {
          const walletOptions = ewData.wallets.map((w) => {
            const balance = w.CURRENT_BALENCE || w.CURRENT_BALANCE || w.BALANCE || w.AMOUNT || 0;
            return {
              value: w.ID ?? w.id,
              label: formatCashWalletDropdownLabel(w, balance),
              data: w,
            };
          });
          setWallets(walletOptions);
      }
    } catch (_) {}
  };

  useEffect(() => {
    const onFundsRefresh = () => {
      refreshLocalWallets();
    };
    window.addEventListener("mfg:funds-refresh", onFundsRefresh);
    return () => window.removeEventListener("mfg:funds-refresh", onFundsRefresh);
  }, []);

  const resetCashInForm = () => {
    setErrors({});
    setAmount("");
    setAccount(null);
    setWallet(null);
    setPaymentMethod(null);
    setSelectedPaymentMethodData(null);
    setDocumentFile(null);
    setDocumentFileName("");
  };

  const goToDashboardAfterWhish = useCallback(
    (result) => {
      if (!claimWhishReturnHandling(result, whishRequestToken || "cashin")) return;
      markWhishReturnActivity();
      clearHomePageInfosClientCache();
      setWhishWaiting(false);
      setWhishRequestToken("");
      resetCashInForm();
      navigate("/pages/dashboard", { replace: true });
      if (result === "success") {
        showSuccess(
          t("transaction_succeeded", {
            ns: "mobile",
            defaultValue: "Transaction succeeded.",
          })
        );
      } else if (result === "failed") {
        showError(
          t("transaction_failed", {
            ns: "mobile",
            defaultValue: "Transaction failed.",
          })
        );
      }
      try {
        window.dispatchEvent(new Event("mfg:funds-refresh"));
        window.dispatchEvent(new Event("dashboard:refresh"));
      } catch (_) {}
    },
    [navigate, showSuccess, showError, t, whishRequestToken]
  );

  useEffect(() => {
    if (!whishWaiting || !whishRequestToken) return undefined;

    let stopped = false;
    const startedAt = Date.now();
    const timeoutMs = 10 * 60 * 1000;

    const readStatus = (raw) =>
      Number(raw?.STATUS ?? raw?.status ?? raw?.STATUS_CODE ?? NaN);

    const tick = async () => {
      if (stopped) return;
      try {
        const res = await CashInRequestController.getCashInByRequestToken(whishRequestToken);
        const raw = res.items?.[0]?.raw;
        const status = readStatus(raw);
        if (
          status === TransactionStatus.COMPLETED ||
          status === TransactionStatus.COLLECTED
        ) {
          goToDashboardAfterWhish("success");
          return;
        }
        if (
          status === TransactionStatus.FAILED ||
          status === TransactionStatus.REJECTED ||
          status === TransactionStatus.CANCELED
        ) {
          goToDashboardAfterWhish("failed");
          return;
        }
      } catch (_) {}

      if (!stopped && Date.now() - startedAt >= timeoutMs) {
        showWarning(
          "Payment in progress",
          t("cash_in_whish_still_processing", {
            ns: "mobile",
            defaultValue: "Payment is still processing. Opening your dashboard…",
          })
        );
        goToDashboardAfterWhish("success");
      }
    };

    tick();
    const intervalId = window.setInterval(tick, 2500);
    return () => {
      stopped = true;
      window.clearInterval(intervalId);
    };
  }, [whishWaiting, whishRequestToken, goToDashboardAfterWhish, showWarning, t]);

  const handlePasswordDialogClose = async (result, data = {}) => {
    setShowPasswordPopup(false);
    setPasswordDialogData(null);
    
    if (result === "ok" || result === "need_admin") {
      clearHomePageInfosClientCache();
      const url = data?.url;
      const whish = isWhishMethod(selectedPaymentMethodData);
      if (result === "ok" && url && whish) {
        const requestToken =
          data?.transactionId || passwordDialogData?.requestToken || "";
        showWarning(
          "Request Submitted",
          t("cash_in_whish_complete_in_popup", {
            ns: "mobile",
            defaultValue: "Cash-in submitted. Complete your payment in the new tab. This page will open your dashboard when it finishes.",
          })
        );
        const paymentWindow = data?.paymentWindow;
        const paymentWindowOpen = paymentWindow && !paymentWindow.closed;
        if (!paymentWindowOpen) {
          window.location.assign(url);
          return;
        }
        if (requestToken) {
          setWhishRequestToken(requestToken);
          setWhishWaiting(true);
        }
        return;
      } else {
        showWarning("Request Submitted", result === "ok" ? "Cash-in request submitted successfully." : "Cash-in pending admin approval.");
      }
      resetCashInForm();
    } else if (result === "canceled") {
      showWarning("Action Canceled", "Transaction canceled.");
      resetCashInForm();
    } else if (result === "failed") {
      showError("Error Occurred", "Transaction failed. Please try again.");
    }

    // Always refresh wallets to keep balances up to date
    await refreshLocalWallets();
  };

  const submitCashInRequest = async () => {
    const type = activeTab === "mt5" ? TransactionType.CASHIN_MT5 : TransactionType.CASHIN;
    const walletId = activeTab === "mt5"
      ? (typeof account === "object" ? account.value : account)
      : (typeof wallet === "object" ? wallet.value : wallet);
    const method = selectedPaymentMethodData?.METHOD_ID ?? (typeof paymentMethod === "object" ? paymentMethod?.value : paymentMethod);
    const amountValue = parseFloat(amount.trim().replace(/,/g, ""));

    try {
      setProcessing(true);
      setErrors({});

      const files = {};
      if (selectedPaymentMethodData?.REQUIRED_DOCUMENT === 1 && documentFile) {
        files.CASHIN_FILE = documentFile;
      }
      const payload = { type, amount: amountValue, walletId, method, phoneNumber: null };

      const res = await createCashInOperation(payload, files);
      const d = res.decryptedData || res.data || {};
      const code = Number(d.CODE);

      if (code === AppConstants.responseOk || code === 200) {
        setErrors({});
        const reqToken = d.REQUEST_TOKEN;
        const received = typeof d.RECEIVED_AMOUNT === "number" ? d.RECEIVED_AMOUNT : parseFloat(d.RECEIVED_AMOUNT) || 0;
        let sym = "";
        if (activeTab === "mt5" && account) {
          const sel = tradingAccounts.find((a) => a.value === (typeof account === "object" ? account.value : account));
          const ad = sel?.data;
          sym = ad?.currencies?.CURRENCY_SYMBOL || ad?.CURRENCY_SYMBOL || "";
        } else if (activeTab === "wallet" && wallet) {
          const sel = wallets.find((w) => w.value === (typeof wallet === "object" ? wallet.value : wallet));
          const wd = sel?.data;
          sym = wd?.currencies?.CURRENCY_SYMBOL || wd?.CURRENCY_SYMBOL || "";
        }
        const firstMsg =
          `You will cash in ${amountValue} ${sym}, you will receive ${received} ${sym} after commission.`;
        setPasswordDialogData({
          requestToken: reqToken,
          firstMessage: firstMsg,
          secondMessage: "",
        });
        setShowPasswordPopup(true);
      } else if (code === AppConstants.responseNotFound) {
        showError("Error Occurred", "You don't have access to do this operation.");
      } else if (code === AppConstants.responseWaiting) {
        showWarning("Action Required", "Financial data should be added to use this method.");
      } else if (code === AppConstants.responseFailed) {
        showError("Error Occurred", d.MESSAGE || "Transaction failed.");
      } else {
        showError("Error Occurred", d.MESSAGE || "Please try again.");
      }
    } catch (error) {
      showError("Error Occurred", error.message || "Failed to process cash-in. Please try again.");
    } finally {
      setProcessing(false);
    }
  };

  // Clear errors when fields change
  // Helper function to get maximum value
  // For Whish payment method, always return 5000 (hardcoded)
  const getMaxValue = (paymentMethodData, accountData = null) => {
    // Special case: Whish payment method has hardcoded max of 5000
    if (paymentMethodData && (paymentMethodData.ID === 2 || paymentMethodData.RELATED_TO === "whish")) {

      return 5000;
    }
    
    // For other payment methods, return null (no max limit)
    return null;
  };

  // Helper function to get minimum value from RANGE, MIN, or restrictions
  // Can check paymentMethodData or accountData/walletData (for account/wallet's currencies.RANGE)
  // For Wallet tab: checks restrictions with FOR = 0 (CASHIN)
  // For MT5 tab: checks restrictions with FOR = 7 (CASHIN_MT5)
  // IMPORTANT: MIN from RANGE must ALWAYS be respected - restriction is only used if greater than MIN
  const getMinValue = (paymentMethodData, accountData = null, activeTab = "wallet") => {
    // Get currency ID for restriction matching
    const currencyId = accountData?.currencies?.ID || 
                       accountData?.currencies?.CURRENCY_ID || 
                       accountData?.CURRENCY_ID ||
                       paymentMethodData?.currencies?.ID ||
                       paymentMethodData?.currencies?.CURRENCY_ID ||
                       paymentMethodData?.CURRENCY_ID;
    
    const walletData = accountData || paymentMethodData;
    
    // FIRST: Get MIN from wallet/account's currencies.RANGE (this is ALWAYS required)
    let minFromRange = null;
    if (accountData && accountData.currencies && accountData.currencies.RANGE) {
      try {
        const range = typeof accountData.currencies.RANGE === "string" 
          ? JSON.parse(accountData.currencies.RANGE) 
          : accountData.currencies.RANGE;
        if (range && typeof range === "object" && range.MIN !== undefined) {
          minFromRange = parseFloat(range.MIN);
          if (!isNaN(minFromRange)) {

          } else {
            minFromRange = null;
          }
        }
      } catch (e) {

      }
    }
    
    // Then try payment method data
    if (!paymentMethodData) {

      return null;
    }

    // Check RANGE in currencies object first (RANGE might be nested in currencies)
    let rangeSource = null;
    if (paymentMethodData.currencies) {
      // Check if currencies is an array
      if (Array.isArray(paymentMethodData.currencies)) {
        // If it's an array, check the first element
        const firstCurrency = paymentMethodData.currencies[0];
        if (firstCurrency && firstCurrency.RANGE) {
          rangeSource = firstCurrency.RANGE;

        }
      } 
      // Check if currencies is an object with RANGE property
      else if (typeof paymentMethodData.currencies === "object" && paymentMethodData.currencies.RANGE) {
        rangeSource = paymentMethodData.currencies.RANGE;

      } else if (typeof paymentMethodData.currencies === "object") {
        // Log for debugging
      }
    }
    
    // Fallback to direct RANGE property
    if (!rangeSource && paymentMethodData.RANGE) {
      rangeSource = paymentMethodData.RANGE;

    }
    
    if (!rangeSource) {

    }
    
    // Check RANGE first (JSON string like "{\"MAX\":100,\"MIN\":1}")
    if (rangeSource) {
      try {
        let range = rangeSource;
        
        // If it's a string, parse it
        if (typeof range === "string") {
          // Remove any extra whitespace
          range = range.trim();
          // Parse the JSON string
          range = JSON.parse(range);

        }
        
        // If it's already an object, use it directly
        if (range && typeof range === "object" && range !== null) {

          // Check for MIN property (case-insensitive check)
          const minValue = range.MIN !== undefined && range.MIN !== null 
            ? range.MIN 
            : (range.min !== undefined && range.min !== null ? range.min : null);
          
          if (minValue !== null && minValue !== undefined) {
            const parsedMin = parseFloat(minValue);
            if (!isNaN(parsedMin)) {

              if (minFromRange == null || parsedMin < minFromRange) {
                minFromRange = parsedMin;
              }
            } else {

            }
          }
        } else {

        }
      } catch (e) {

      }
    }
    
    // Fallback to MIN if RANGE is not available
    if (minFromRange == null && paymentMethodData.MIN !== undefined && paymentMethodData.MIN !== null) {
      const minValue = parseFloat(paymentMethodData.MIN);
      if (!isNaN(minValue)) {

        minFromRange = minValue;
      }
    }
    
    // SECOND: Check restrictions and compare with minFromRange
    // IMPORTANT: MIN from RANGE is ALWAYS the base requirement
    // Restriction is only used if it's GREATER than the MIN from range
    // Check both walletData.currencies.restriction and walletData.restriction (for different data structures)
    // For Wallet tab: use FOR = 0 (CASHIN)
    // For MT5 tab: use FOR = 7 (CASHIN_MT5)
    const restrictions = walletData?.currencies?.restriction || walletData?.restriction;
    if (restrictions && Array.isArray(restrictions)) {
      const feesFor = activeTab === "mt5" ? FeesFor.CASHIN_MT5 : FeesFor.CASHIN; // 7 for MT5, 0 for Wallet
      const matchingRestriction = restrictions.find(rest => {
        const restFor = rest.FOR !== undefined ? Number(rest.FOR) : null;
        const restCurrencyId = rest.CURRENCY_ID !== undefined ? Number(rest.CURRENCY_ID) : null;
        return restFor === feesFor && 
               (currencyId == null || restCurrencyId == null || restCurrencyId === Number(currencyId));
      });
      
      if (matchingRestriction && matchingRestriction.AMOUNT !== undefined) {
        const restrictionAmount = parseFloat(matchingRestriction.AMOUNT);
        if (!isNaN(restrictionAmount)) {
          // If we have a min from range, restriction must be greater than it
          if (minFromRange != null) {
            if (restrictionAmount > minFromRange) {
              return restrictionAmount;
            } else {
              return minFromRange;
            }
          } else {
            // No min from range, use restriction amount
            return restrictionAmount;
          }
        }
      }
    }
    
    // Return min from range if available (always required)
    if (minFromRange != null) {
      return minFromRange;
    }

    return null;
  };

  const handleAmountChange = (e) => {
    const newAmount = e.target.value;
    setAmount(newAmount);
    
    // Clear amount error
    if (errors.amount) {
      setErrors({ ...errors, amount: null });
    }
    
    // Validate against minimum from RANGE or MIN
    // Check account's currencies.RANGE first, then payment method's RANGE/MIN
    if (newAmount) {
      let selectedAccountData = null;
      let selectedWalletData = null;
      if (activeTab === "mt5" && account) {
        // Find the selected account data
        const selectedAccount = tradingAccounts.find(
          (acc) => acc.value === (typeof account === "object" ? account.value : account)
        );
        selectedAccountData = selectedAccount?.data || null;
      } else if (activeTab === "wallet" && wallet) {
        // Find the selected wallet data
        const selectedWallet = wallets.find(
          (w) => w.value === (typeof wallet === "object" ? wallet.value : wallet)
        );
        selectedWalletData = selectedWallet?.data || null;
      }
      
      const minValue = getMinValue(selectedPaymentMethodData, selectedAccountData || selectedWalletData, activeTab);
      const maxValue = getMaxValue(selectedPaymentMethodData, selectedAccountData);
      
      if (newAmount) {
        const amountValue = parseFloat(newAmount);
        if (!isNaN(amountValue)) {
          // Validate minimum
          if (minValue !== null && !isNaN(minValue) && amountValue <= minValue) {
            const currencySymbol = selectedAccountData?.currencies?.CURRENCY_SYMBOL || 
                                 selectedPaymentMethodData?.CURRENCY_SYMBOL || "";
            setErrors({ 
              ...errors, 
              amount: `Amount must be greater than ${minValue}${currencySymbol ? ` ${currencySymbol}` : ""}`
            });
          }
          // Validate maximum
          else if (maxValue !== null && !isNaN(maxValue) && amountValue > maxValue) {
            const currencySymbol = selectedAccountData?.currencies?.CURRENCY_SYMBOL || 
                                 selectedPaymentMethodData?.CURRENCY_SYMBOL || "";
            setErrors({ 
              ...errors, 
              amount: `Max is ${maxValue.toLocaleString()}${currencySymbol ? ` ${currencySymbol}` : ""}`
            });
          }
          // Clear error if valid
          else if (errors.amount) {
            setErrors({ ...errors, amount: null });
          }
        }
      }
    }
  };

  const handleAccountChange = (value) => {
    setAccount(value);
    if (errors.account) {
      setErrors({ ...errors, account: null });
    }
  };

  const handleWalletChange = (value) => {
    setWallet(value);
    const selectedWalletValue = typeof value === "object" ? value?.value : value;
    const selectedWalletData = wallets.find((w) => w.value === selectedWalletValue)?.data ?? null;
    const walletCurrencyCode = getWalletCurrencyCode(selectedWalletData);
    const canUseWhish = walletCurrencyCode === "USD" || walletCurrencyCode === "LBP";

    if (selectedPaymentMethodData && isWhishMethod(selectedPaymentMethodData) && !canUseWhish) {
      setPaymentMethod(null);
      setSelectedPaymentMethodData(null);
      setErrors((prev) => ({ ...prev, paymentMethod: "Whish supports only USD or LBP wallets." }));
    }

    if (errors.wallet) {
      setErrors({ ...errors, wallet: null });
    }
  };

  const handlePaymentMethodChange = (value) => {
    setPaymentMethod(value);
    if (errors.paymentMethod) {
      setErrors({ ...errors, paymentMethod: null });
    }
    
    // Find the full payment method data for the selected value
    const selectedMethod = paymentMethods.find(
      (method) => method.value === (typeof value === "object" ? value.value : value)
    );
    
    const paymentMethodData = selectedMethod?.data || null;

    setSelectedPaymentMethodData(paymentMethodData);
    
    // Reset document file when payment method changes
    if (paymentMethodData?.REQUIRED_DOCUMENT !== 1) {
      setDocumentFile(null);
      setDocumentFileName("");
    }
    
    // Validate amount against minimum from RANGE or MIN if amount is already entered
    // Check account's currencies.RANGE first, then payment method's RANGE/MIN
    if (amount) {
      let selectedAccountData = null;
      let selectedWalletData = null;
      if (activeTab === "mt5" && account) {
        // Find the selected account data
        const selectedAccount = tradingAccounts.find(
          (acc) => acc.value === (typeof account === "object" ? account.value : account)
        );
        selectedAccountData = selectedAccount?.data || null;
      } else if (activeTab === "wallet" && wallet) {
        // Find the selected wallet data
        const selectedWallet = wallets.find(
          (w) => w.value === (typeof wallet === "object" ? wallet.value : wallet)
        );
        selectedWalletData = selectedWallet?.data || null;
      }
      
      const minValue = getMinValue(paymentMethodData, selectedAccountData || selectedWalletData, activeTab);
      const maxValue = getMaxValue(paymentMethodData, selectedAccountData);
      
      const amountValue = parseFloat(amount);
      if (!isNaN(amountValue)) {
        // Validate minimum
        if (minValue !== null && !isNaN(minValue) && amountValue <= minValue) {
          const currencySymbol = selectedAccountData?.currencies?.CURRENCY_SYMBOL || 
                               paymentMethodData?.CURRENCY_SYMBOL || "";
          setErrors({ 
            ...errors, 
            amount: `Amount must be greater than ${minValue}${currencySymbol ? ` ${currencySymbol}` : ""}`
          });
        }
        // Validate maximum
        else if (maxValue !== null && !isNaN(maxValue) && amountValue > maxValue) {
          const currencySymbol = selectedAccountData?.currencies?.CURRENCY_SYMBOL || 
                               paymentMethodData?.CURRENCY_SYMBOL || "";
          setErrors({ 
            ...errors, 
            amount: `Max is ${maxValue.toLocaleString()}${currencySymbol ? ` ${currencySymbol}` : ""}`
          });
        }
        // Clear error if valid
        else if (errors.amount) {
          setErrors({ ...errors, amount: null });
        }
      }
    }
  };

  const selectedAccountData = useMemo(() => {
    if (!account) return null;
    return (
      tradingAccounts.find(
        (acc) => acc.value === (typeof account === "object" ? account.value : account)
      )?.data || null
    );
  }, [account, tradingAccounts]);

  const selectedWalletData = useMemo(() => {
    if (!wallet) return null;
    return (
      wallets.find((w) => w.value === (typeof wallet === "object" ? wallet.value : wallet))
        ?.data || null
    );
  }, [wallet, wallets]);

  const selectedFundingSourceData = activeTab === "mt5" ? selectedAccountData : selectedWalletData;
  const computedMinValue = getMinValue(selectedPaymentMethodData, selectedFundingSourceData, activeTab);
  const computedMaxValue = getMaxValue(selectedPaymentMethodData, selectedFundingSourceData);
  const toNumberSafe = (value, fallback = 0) => {
    const parsed =
      typeof value === "string" ? Number(value.replace(/,/g, "").trim()) : Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };
  const usdBaseWalletData = useMemo(() => {
    return (
      wallets.find((w) => {
        const walletCurrency = (
          w?.data?.currencies?.CURRENCY_CODE ||
          w?.data?.CURRENCY_CODE ||
          ""
        )
          .toString()
          .toUpperCase();
        return walletCurrency === "USD";
      })?.data || null
    );
  }, [wallets]);
  const transactionLimitWalletData =
    usdBaseWalletData || selectedWalletData || selectedFundingSourceData || null;
  const ctsMaxAmount = toNumberSafe(
    transactionLimitWalletData?.currencies?.cts_max_amount ??
      transactionLimitWalletData?.currencies?.CTS_MAX_AMOUNT ??
      transactionLimitWalletData?.currencies?.CTS_MAX ??
      transactionLimitWalletData?.CTS_MAX_AMOUNT,
    0
  );
  const totalCashInToday = toNumberSafe(
    transactionLimitWalletData?.TOTAL_CASHIN_TODAY ??
      transactionLimitWalletData?.totalCashinToday ??
      transactionLimitWalletData?.TOTAL_CASH_IN_TODAY ??
      transactionLimitWalletData?.currencies?.TOTAL_CASHIN_TODAY,
    0
  );
  const transactionLimitTarget = ctsMaxAmount > 0 ? ctsMaxAmount : computedMaxValue || 50000;
  const transactionLimitProgress =
    transactionLimitTarget > 0
      ? Math.min((totalCashInToday / transactionLimitTarget) * 100, 100)
      : 0;
  const isTransactionLimitReached =
    transactionLimitTarget > 0 && totalCashInToday >= transactionLimitTarget;
  const getLimitMinBySource = (sourceData, tabKey = "wallet") => {
    if (!sourceData) return null;

    const parseRangeMin = (rangeValue) => {
      if (!rangeValue) return null;
      try {
        const parsedRange =
          typeof rangeValue === "string" ? JSON.parse(rangeValue) : rangeValue;
        const minVal = parsedRange?.MIN ?? parsedRange?.min;
        const numericMin = Number(minVal);
        return Number.isFinite(numericMin) ? numericMin : null;
      } catch (_) {
        return null;
      }
    };

    const currencyId =
      sourceData?.currencies?.ID ??
      sourceData?.currencies?.CURRENCY_ID ??
      sourceData?.CURRENCY_ID ??
      null;
    const minFromRange = parseRangeMin(sourceData?.currencies?.RANGE ?? sourceData?.RANGE);
    const restrictions = sourceData?.currencies?.restriction || sourceData?.restriction || [];
    const feesFor = tabKey === "mt5" ? FeesFor.CASHIN_MT5 : FeesFor.CASHIN;

    const matchedRestriction = Array.isArray(restrictions)
      ? restrictions.find((rest) => {
          const restFor = Number(rest?.FOR);
          const restCurrencyId =
            rest?.CURRENCY_ID != null ? Number(rest.CURRENCY_ID) : null;
          const sourceCurrencyId =
            currencyId != null ? Number(currencyId) : null;
          return (
            restFor === feesFor &&
            (restCurrencyId == null ||
              sourceCurrencyId == null ||
              restCurrencyId === sourceCurrencyId)
          );
        })
      : null;
    const restrictionMin = Number(matchedRestriction?.AMOUNT);
    const validRestrictionMin = Number.isFinite(restrictionMin) ? restrictionMin : null;

    if (minFromRange != null && validRestrictionMin != null) {
      return Math.max(minFromRange, validRestrictionMin);
    }
    if (validRestrictionMin != null) return validRestrictionMin;
    return minFromRange;
  };
  const transactionLimitMinValue =
    activeTab === "mt5"
      ? getLimitMinBySource(selectedAccountData, "mt5")
      : getLimitMinBySource(selectedWalletData || selectedFundingSourceData, "wallet") ??
        computedMinValue;
  const activeLimitCurrencyCode = (
    selectedFundingSourceData?.currencies?.CURRENCY_CODE ||
    selectedFundingSourceData?.CURRENCY_CODE ||
    "USD"
  )
    .toString()
    .toUpperCase();
  const activeLimitCurrencySymbol = (
    selectedFundingSourceData?.currencies?.CURRENCY_SYMBOL ||
    selectedFundingSourceData?.CURRENCY_SYMBOL ||
    "$"
  ).toString();
  const normalizedLimitCurrencySymbol = activeLimitCurrencySymbol.trim() || "$";
  const formatLimitAmount = (value) => {
    const numericValue = toNumberSafe(value, 0);
    const amountText = numericValue.toLocaleString();
    if (normalizedLimitCurrencySymbol === "$" || normalizedLimitCurrencySymbol === "€" || normalizedLimitCurrencySymbol === "£") {
      return `${normalizedLimitCurrencySymbol}${amountText}`;
    }
    return `${amountText} ${normalizedLimitCurrencySymbol}`;
  };

  const getMethodDisplayMeta = (methodOption) => {
    const methodData = methodOption?.data || {};
    const methodLabel = extractValue(
      methodOption?.label || methodData?.METHOD_NAME || methodData?.NAME || "Payment Method"
    );
    const normalized = methodLabel.toLowerCase();

    const processingValue = extractValue(
      methodData?.PROCESSING || methodData?.processing || methodData?.PROCESSING_TIME
    );
    let eta = (processingValue || "").toString().trim();

    if (normalized.includes("bank")) {
      return {
        key: "bank",
        icon: "fa-building-columns",
        eta: eta || "1-3 business days",
      };
    }
    if (
      normalized.includes("crypto") ||
      normalized.includes("usdt") ||
      normalized.includes("bitcoin") ||
      normalized.includes("ethereum")
    ) {
      return { key: "crypto", icon: "fa-coins", eta: eta || "Instant" };
    }
    if (normalized.includes("card") || normalized.includes("visa") || normalized.includes("master")) {
      return { key: "card", icon: "fa-credit-card", eta: eta || "Instant" };
    }

    if (!eta) eta = "Instant";
    return { key: "other", icon: "fa-wallet", eta };
  };
  
  const handleDocumentChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setDocumentFile(file);
      setDocumentFileName(file.name);
      if (errors.document) {
        setErrors({ ...errors, document: null });
      }
    }
  };
  
  const handleRemoveDocument = () => {
    setDocumentFile(null);
    setDocumentFileName("");
    // Reset the file input
    const fileInput = document.querySelector('input[type="file"][name="document"]');
    if (fileInput) {
      fileInput.value = "";
    }
  };
  
  // Copy clipboard to clipboard
  const handleCopyClipboard = async () => {
    if (selectedPaymentMethodData?.CLIPBOARD) {
      try {
        await navigator.clipboard.writeText(selectedPaymentMethodData.CLIPBOARD);

        showSuccess(
          t("successfully_operation", { ns: "mobile" }),
          t("cash_in_clipboard_copied", { ns: "mobile" })
        );
      } catch (error) {
        showError(
          t("error_operation", { ns: "mobile" }),
          t("cash_in_clipboard_copy_failed", { ns: "mobile" })
        );
      }
    }
  };

  return (
    <div className="cash-in-page">
      <Sidebar user={user} />
      <div className="cash-in-content">
        {loading && (
          <div className="cash-in-loading-message">
            Loading...
          </div>
        )}
        
        <div className="cash-in-container cash-in-container--styled">
          {/* Tab Content */}
          <div className="cash-in-tab-content cash-in-tab-content--styled">
            {/* Show message if current tab is locked */}
            {whishWaiting && (
              <div className="cash-in-locked-message cash-in-whish-waiting">
                <i className="fa-solid fa-spinner fa-spin"></i>
                <p>
                  {t("cash_in_whish_waiting_for_payment", {
                    ns: "mobile",
                    defaultValue:
                      "Complete your payment in the other tab. This page will open your dashboard when it succeeds.",
                  })}
                </p>
              </div>
            )}
            {((activeTab === "mt5" && !canCashInMT5) || (activeTab === "wallet" && !canCashInWallet)) && !whishWaiting && (
              <div className="cash-in-locked-message">
                <i className="fa-solid fa-lock"></i>
                <p>{t("you_dont_have_access_to_do_this_operation", { ns: "mobile" })}</p>
              </div>
            )}
            {/* Show form only if current tab has permission */}
            {!whishWaiting && ((activeTab === "mt5" && canCashInMT5) || (activeTab === "wallet" && canCashInWallet)) && (
            <div className="cash-in-layout-grid">
              <div className="cash-in-form-panel">
                <div className="cash-in-panel-header">
                  <h2>{t("cash_in_new_deposit_title", { ns: "mobile" })}</h2>
                  <p>{t("cash_in_new_deposit_subtitle", { ns: "mobile" })}</p>
                </div>
                {/* Tabs now part of New Deposit card */}
                <div className="cash-in-tabs" data-field-tour="cash_in:tabs">
                  <button
                    className={`cash-in-tab ${activeTab === "mt5" && canCashInMT5 ? "cash-in-tab--active" : ""} ${!canCashInMT5 ? "cash-in-tab--locked" : ""}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (!canCashInMT5) return;
                      setActiveTab("mt5");
                      setAmount("");
                      if (errors.wallet) setErrors({ ...errors, wallet: null });
                      if (errors.amount) setErrors({ ...errors, amount: null });
                    }}
                    type="button"
                    disabled={!canCashInMT5}
                    title={!canCashInMT5 ? "You don't have permission to use MT5 cash in" : ""}
                    style={{ cursor: !canCashInMT5 ? "not-allowed" : "pointer" }}
                  >
                    MT5
                    {!canCashInMT5 && <i className="fa-solid fa-lock cash-in-tab-lock-icon"></i>}
                  </button>
                  <button
                    className={`cash-in-tab ${activeTab === "wallet" && canCashInWallet ? "cash-in-tab--active" : ""} ${!canCashInWallet ? "cash-in-tab--locked" : ""}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (!canCashInWallet) return;
                      setActiveTab("wallet");
                      setAmount("");
                      if (errors.account) setErrors({ ...errors, account: null });
                      if (errors.amount) setErrors({ ...errors, amount: null });
                    }}
                    type="button"
                    disabled={!canCashInWallet}
                    title={!canCashInWallet ? "You don't have permission to use Wallet cash in" : ""}
                    style={{ cursor: !canCashInWallet ? "not-allowed" : "pointer" }}
                  >
                    {t("wallet", { ns: "mobile" })}
                    {!canCashInWallet && <i className="fa-solid fa-lock cash-in-tab-lock-icon"></i>}
                  </button>
                </div>
                {activeTab === "mt5" && manualCorrectionEnabled && (
                  <ManualCorrectionWarning
                    animate={isSelectedMt5BalanceNegative(account)}
                    extraMessage={
                      isSelectedMt5BalanceNegative(account)
                        ? t("selected_mt5_balance_below_zero", { ns: "mobile" })
                        : null
                    }
                  />
                )}

                <div className="cash-in-form">
                  {/* Amount Field */}
                  <div className="cash-in-form-group" data-field-tour="cash_in:amount">
                    <label className="cash-in-form-label">{t("amount", { ns: "mobile" })}</label>
                    <div className="cash-in-input-wrapper">
                      <span className="cash-in-input-icon">
                        <i className="fa-solid fa-dollar-sign"></i>
                      </span>
                      <CustomInput
                        type="text"
                        placeholder={t("enter_the_amount_to_cashed_in", { ns: "mobile" })}
                        value={amount}
                        onChange={handleAmountChange}
                        className={`cash-in-input ${errors.amount ? "cash-in-input-error" : ""}`}
                        disabled={processing || !canProceed}
                      />
                    </div>
                    {/* Minimum Amount Display - Only show when amount is below min or above max */}
                    {amount && (() => {
                      const amountValue = parseFloat(amount);

                      // Only show if amount is invalid (below min or above max)
                      if (!isNaN(amountValue)) {
                        const currencySymbol =
                          selectedFundingSourceData?.currencies?.CURRENCY_SYMBOL ||
                          selectedPaymentMethodData?.CURRENCY_SYMBOL ||
                          "";

                        // Show if amount is below minimum
                        if (
                          computedMinValue !== null &&
                          !isNaN(computedMinValue) &&
                          amountValue <= computedMinValue
                        ) {
                          return (
                            <div className="cash-in-min-amount">
                              Minimum Amount: {computedMinValue}
                              {currencySymbol ? ` ${currencySymbol}` : ""}
                            </div>
                          );
                        }

                        // Show if amount is above maximum
                        if (
                          computedMaxValue !== null &&
                          !isNaN(computedMaxValue) &&
                          amountValue > computedMaxValue
                        ) {
                          return (
                            <div className="cash-in-min-amount">
                              Maximum Amount: {computedMaxValue.toLocaleString()}
                              {currencySymbol ? ` ${currencySymbol}` : ""}
                            </div>
                          );
                        }
                      }

                      return null;
                    })()}
                  </div>

                  {/* Account/Wallet Field */}
                  <div
                    className="cash-in-form-group"
                    data-field-tour={activeTab === "mt5" ? "cash_in:account" : "cash_in:wallet"}
                  >
                    <label className="cash-in-form-label">
                      {activeTab === "mt5"
                        ? t("account", { ns: "mobile" })
                        : t("wallet", { ns: "mobile" })}
                    </label>
                    {activeTab === "mt5" ? (
                      <CustomDropdown
                    options={tradingAccounts}
                    value={account}
                    onChange={handleAccountChange}
                    placeholder={t("select_an_account", { ns: "mobile" })}
                    name="account"
                    className={`cash-in-dropdown ${errors.account ? "dropdown-error" : ""}`}
                    disabled={processing || !canProceed}
                    searchable={true}
                    renderOption={(option, isSelected) => {
                      const accountData = typeof option === "object" && option.data ? option.data : null;
                      // Get TRADING_ID from nested account object or direct property
                      const tradingId = accountData?.account?.account_information?.TRADING_ID || accountData?.TRADING_ID || "";
                      const amount = accountData?.AMOUNT || accountData?.CURRENT_BALENCE || accountData?.CURRENT_BALANCE || accountData?.BALANCE || 0;
                      // Get CURRENCY_SYMBOL from account or nested currencies object
                      const currencySymbol = accountData?.currencies?.CURRENCY_SYMBOL || 
                                           accountData?.CURRENCY_SYMBOL || 
                                           "";
                      const displayText = `${tradingId} (${amount} ${currencySymbol})`;
                      
                      return (
                        <div className="cash-in-account-option">
                          <img src={mt5Logo} alt="MT5" className="cash-in-account-logo" />
                          <span className="cash-in-account-text">{displayText}</span>
                          {isSelected && <i className="fa-solid fa-check custom-dropdown-check"></i>}
                        </div>
                      );
                    }}
                    getDisplayValue={(option) => {
                      if (!option) return "";
                      if (typeof option === "object" && option.data) {
                        const acc = option.data;
                        // Get TRADING_ID from nested account object or direct property
                        const tradingId = acc.account?.account_information?.TRADING_ID || acc.TRADING_ID || "";
                        const amount = acc.AMOUNT || acc.CURRENT_BALENCE || acc.CURRENT_BALANCE || acc.BALANCE || 0;
                        // Get CURRENCY_SYMBOL from account or nested currencies object
                        const currencySymbol = acc.currencies?.CURRENCY_SYMBOL || 
                                             acc.CURRENCY_SYMBOL || 
                                             "";
                        return `${tradingId} (${amount} ${currencySymbol})`;
                      }
                      return typeof option === "object" ? option.label : option;
                    }}
                    renderValue={(option, placeholderText) => {
                      if (!option) {
                        return (
                          <span className="custom-dropdown-placeholder">
                            {placeholderText || t("select_an_account", { ns: "mobile", defaultValue: "Select Account" })}
                          </span>
                        );
                      }
                      const accountData = typeof option === "object" && option.data ? option.data : null;
                      if (!accountData) {
                        return <span>{typeof option === "object" ? option.label : option}</span>;
                      }
                      // Get TRADING_ID from nested account object or direct property
                      const tradingId = accountData.account?.account_information?.TRADING_ID || accountData.TRADING_ID || "";
                      const amount = accountData.AMOUNT || accountData.CURRENT_BALENCE || accountData.CURRENT_BALANCE || accountData.BALANCE || 0;
                      // Get CURRENCY_SYMBOL from account or nested currencies object
                      const currencySymbol = accountData.currencies?.CURRENCY_SYMBOL || 
                                           accountData.CURRENCY_SYMBOL || 
                                           "";
                      const displayText = `${tradingId} (${amount} ${currencySymbol})`;
                      
                      return (
                        <div className="cash-in-account-trigger">
                          <img src={mt5Logo} alt="MT5" className="cash-in-account-logo" />
                          <span className="cash-in-account-text">{displayText}</span>
                        </div>
                      );
                    }}
                  />
                    ) : (
                      <CustomDropdown
                     options={wallets}
                     value={wallet}
                     onChange={handleWalletChange}
                     placeholder={t("select_a_wallet", { ns: "mobile" })}
                     name="wallet"
                     className={`cash-in-dropdown ${errors.wallet ? "dropdown-error" : ""}`}
                     disabled={processing || !canProceed}
                     renderOption={(option, isSelected) => {
                       const walletData = typeof option === "object" && option.data ? option.data : null;
                       if (!walletData) {
                         return <span>{typeof option === "object" ? option.label : option}</span>;
                       }
                       
                      const balance = walletData.CURRENT_BALENCE || walletData.CURRENT_BALANCE || walletData.BALANCE || walletData.AMOUNT || 0;
                      const balanceNum = typeof balance === 'string' 
                        ? parseFloat(balance.replace(/,/g, '')) 
                        : parseFloat(balance);
                      const formattedBalance = isNaN(balanceNum) ? 0 : balanceNum;
                      const { flagCode: flagCountryCode, code: currencyCode, sym: currencySymbol, suffix } =
                        resolveCashWalletCurrencyDisplay(
                          walletData,
                          typeof option === "object" ? option.label : ""
                        );

                      return (
                        <div className="cash-in-wallet-option" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <WalletCurrencyFlag
                            countryCode={flagCountryCode}
                            title={currencyCode || currencySymbol || flagCountryCode || ""}
                          />
                          <span>{formattedBalance.toFixed(2)}{suffix ? ` ${suffix}` : ""}</span>
                          {isSelected && <i className="fa-solid fa-check custom-dropdown-check"></i>}
                        </div>
                      );
                     }}
                    getDisplayValue={(option) => {
                      if (!option) return "";
                      if (typeof option === "object" && option.data) {
                        const walletData = option.data;
                        const balance = walletData.CURRENT_BALENCE || walletData.CURRENT_BALANCE || walletData.BALANCE || walletData.AMOUNT || 0;
                        return formatCashWalletDropdownLabel(walletData, balance);
                      }
                      return typeof option === "object" ? option.label : option;
                    }}
                     renderValue={(option, placeholderText) => {
                       if (!option) {
                         return (
                           <span className="custom-dropdown-placeholder">
                             {placeholderText || t("select_a_wallet", { ns: "mobile", defaultValue: "Select Wallet" })}
                           </span>
                         );
                       }
                       const walletData = typeof option === "object" && option.data ? option.data : null;
                       if (!walletData) {
                         return <span>{typeof option === "object" ? option.label : option}</span>;
                       }
                       
                      const balance = walletData.CURRENT_BALENCE || walletData.CURRENT_BALANCE || walletData.BALANCE || walletData.AMOUNT || 0;
                      const balanceNum = typeof balance === 'string' 
                        ? parseFloat(balance.replace(/,/g, '')) 
                        : parseFloat(balance);
                      const formattedBalance = isNaN(balanceNum) ? 0 : balanceNum;
                      const { flagCode: flagCountryCode, code: currencyCode, sym: currencySymbol, suffix } =
                        resolveCashWalletCurrencyDisplay(
                          walletData,
                          typeof option === "object" ? option.label : ""
                        );

                      return (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <WalletCurrencyFlag
                            countryCode={flagCountryCode}
                            title={currencyCode || currencySymbol || flagCountryCode || ""}
                          />
                          <span>{formattedBalance.toFixed(2)}{suffix ? ` ${suffix}` : ""}</span>
                        </div>
                      );
                     }}
                   />
                    )}
                  </div>

                  {/* Payment Method Field */}
                  <div className="cash-in-form-group" data-field-tour="cash_in:method">
                    <label className="cash-in-form-label">{t("cash_in_method", { ns: "mobile" })}</label>
                    <div className={`cash-in-method-list ${errors.paymentMethod ? "cash-in-method-list--error" : ""}`}>
                      {paymentMethodsLoading ? (
                        <>
                          <div className="cash-in-method-skeleton" />
                          <div className="cash-in-method-skeleton" />
                          <div className="cash-in-method-skeleton" />
                        </>
                      ) : filteredPaymentMethods.length > 0 ? (
                        filteredPaymentMethods.map((methodOption) => {
                          const selectedValue =
                            typeof paymentMethod === "object" ? paymentMethod?.value : paymentMethod;
                          const methodValue =
                            typeof methodOption === "object" ? methodOption?.value : methodOption;
                          const isSelected = selectedValue === methodValue;
                          const meta = getMethodDisplayMeta(methodOption);

                          return (
                            <button
                              key={String(methodValue)}
                              type="button"
                              className={`cash-in-method-item ${isSelected ? "cash-in-method-item--active" : ""}`}
                              onClick={() => {
                                handlePaymentMethodChange(methodOption);
                                setActiveProcessingType(meta.key);
                              }}
                              disabled={processing || !canProceed}
                            >
                              <span className="cash-in-method-item-left">
                                <span
                                  className={`cash-in-method-radio ${isSelected ? "is-selected" : ""}`}
                                  aria-hidden="true"
                                />
                                <i className={`fa-solid ${meta.icon}`} />
                                <span>{extractValue(methodOption?.label || "Payment Method")}</span>
                              </span>
                              <span className="cash-in-method-item-right">{meta.eta}</span>
                            </button>
                          );
                        })
                      ) : (
                        <div className="cash-in-method-empty">No payment methods available.</div>
                      )}
                    </div>
                
                {/* Description below dropdown if not null */}
                {selectedPaymentMethodData && (() => {
                  const rawDescription = selectedPaymentMethodData.DESCRIPTION;
                  
                  // Check if description exists (not null, not undefined, not empty string)
                  if (!rawDescription) {
                    return null;
                  }
                  
                  const descriptionValue = extractValue(rawDescription);
                  
                  // Only show if description has a value after extraction
                  if (descriptionValue && descriptionValue.trim() !== "" && descriptionValue.trim() !== "null") {
                    return (
                      <div
                        className="cash-in-payment-description"
                        dangerouslySetInnerHTML={{ __html: descriptionValue }}
                      />
                    );
                  }
                  return null;
                })()}
                
                {/* Clipboard field - show and make copyable */}
                {selectedPaymentMethodData?.CLIPBOARD && (
                  <div className="cash-in-clipboard-container" data-field-tour="cash_in:details">
                    <label className="cash-in-clipboard-label">
                      {t("cash_in_clipboard_label", { ns: "mobile" })}
                    </label>
                    <div className="cash-in-clipboard-field">
                      <input
                        type="text"
                        value={selectedPaymentMethodData.CLIPBOARD}
                        readOnly
                        className="cash-in-clipboard-input"
                      />
                      <button
                        type="button"
                        onClick={handleCopyClipboard}
                        className="cash-in-clipboard-copy-button"
                        title={t("cash_in_copy_to_clipboard", { ns: "mobile" })}
                      >
                        <i className="fa-solid fa-copy"></i>
                      </button>
                    </div>
                  </div>
                )}
                
                {/* Document attachment - show if REQUIRED_DOCUMENT === 1 */}
                {selectedPaymentMethodData?.REQUIRED_DOCUMENT === 1 && (
                  <div className="cash-in-document-container" data-field-tour="cash_in:document">
                    <label className="cash-in-document-label">
                      {t("select_your_cashin_document", { ns: "mobile" })}
                      {selectedPaymentMethodData.REQUIRED_DOCUMENT === 1 && <span className="cash-in-required"> *</span>}
                    </label>
                    {!documentFile ? (
                      <div className="cash-in-document-upload">
                        <input
                          type="file"
                          name="document"
                          id="document-upload"
                          onChange={handleDocumentChange}
                          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                          className="cash-in-document-input"
                          disabled={processing || !canProceed}
                        />
                        <label 
                          htmlFor="document-upload" 
                          className={`cash-in-document-label-button ${errors.document ? "cash-in-document-error" : ""}`}
                        >
                          <i className="fa-solid fa-upload"></i>
                          <span>{t("select_document", { ns: "mobile" })}</span>
                        </label>
                        {errors.document && (
                          <div className="cash-in-document-error-message">{errors.document}</div>
                        )}
                      </div>
                    ) : (
                      <div className="cash-in-document-selected">
                        <div className="cash-in-document-file-info">
                          <i className="fa-solid fa-file"></i>
                          <span className="cash-in-document-file-name">{documentFileName}</span>
                        </div>
                        <button
                          type="button"
                          onClick={handleRemoveDocument}
                          className="cash-in-document-remove-button"
                          title="Remove document"
                        >
                          <i className="fa-solid fa-times"></i>
                        </button>
                      </div>
                    )}
                  </div>
                )}
                  </div>
                </div>

                {/* Proceed Button - Only show if current tab has permission */}
                {((activeTab === "mt5" && canCashInMT5) || (activeTab === "wallet" && canCashInWallet)) && (
                <div className="cash-in-actions">
                  <CustomButton
                    className="cash-in-proceed-button"
                    variant="primary"
                    onClick={handleProceed}
                    disabled={
                      processing ||
                      !canProceed ||
                      (activeTab === "mt5" && !canCashInMT5) ||
                      (activeTab === "wallet" && !canCashInWallet)
                    }
                  >
                    <span>
                      <i className="fa-solid fa-lock" style={{ marginRight: 8 }} />
                      {locationChecking
                        ? t("cash_in_getting_location", { ns: "mobile" })
                        : processing
                          ? t("loading", { ns: "mobile" })
                          : t("cash_in_confirm_button", { ns: "mobile" })}
                    </span>
                  </CustomButton>
                </div>
                )}
              </div>

              <div className="cash-in-side-panel">
                <div className="cash-in-info-card">
                  <h3>
                    <i className="fa-solid fa-chart-line" />
                    {t("cash_in_transaction_limits", { ns: "mobile" })}
                  </h3>
                  <div className="cash-in-limit-top-row">
                    <div>
                      <span>{t("cash_in_daily_limit", { ns: "mobile" })} ({activeLimitCurrencyCode})</span>
                      <strong>{formatLimitAmount(transactionLimitTarget)}</strong>
                    </div>
                    <div className="cash-in-limit-used">
                      <span>{t("cash_in_used_today", { ns: "mobile" })}</span>
                      <strong className={isTransactionLimitReached ? "is-done" : ""}>
                        {formatLimitAmount(totalCashInToday)}
                      </strong>
                    </div>
                  </div>
                  <div
                    className={`cash-in-limit-progress ${
                      isTransactionLimitReached ? "is-done" : ""
                    }`}
                  >
                    <span
                      style={{
                        width: `${transactionLimitProgress}%`,
                      }}
                    />
                  </div>
                  <ul>
                    <li>
                      <span>{t("cash_in_min_per_transaction", { ns: "mobile" })}</span>
                      <strong>{formatLimitAmount(transactionLimitMinValue || 10)}</strong>
                    </li>
                    {selectedPaymentMethodData && isWhishMethod(selectedPaymentMethodData) && (
                      <li>
                        <span>{t("cash_in_max_per_transaction", { ns: "mobile" })}</span>
                        <strong>
                          {computedMaxValue != null && Number.isFinite(Number(computedMaxValue))
                            ? formatLimitAmount(computedMaxValue)
                            : "-"}
                        </strong>
                      </li>
                    )}
                    <li>
                      <span>{t("cash_in_cts_threshold", { ns: "mobile" })}</span>
                      <strong>
                        {ctsMaxAmount > 0 ? formatLimitAmount(ctsMaxAmount) : "-"}
                      </strong>
                    </li>
                  </ul>
                </div>

                <div className="cash-in-info-card">
                  <h3>
                    <i className="fa-solid fa-clock" />
                    {t("cash_in_processing_time", { ns: "mobile" })}
                  </h3>
                  <ul className="cash-in-processing-list">
                    {paymentMethodsLoading ? (
                      <>
                        <li>
                          <span><i className="fa-solid fa-clock" />{t("cash_in_loading_methods", { ns: "mobile" })}</span>
                          <strong>...</strong>
                        </li>
                      </>
                    ) : filteredPaymentMethods.length > 0 ? (
                      filteredPaymentMethods.map((methodOption) => {
                        const meta = getMethodDisplayMeta(methodOption);
                        const methodValue =
                          typeof methodOption === "object" ? methodOption?.value : methodOption;
                        const selectedValue =
                          typeof paymentMethod === "object" ? paymentMethod?.value : paymentMethod;
                        const label = extractValue(methodOption?.label || "Method");
                        const isActive = selectedValue === methodValue;

                        return (
                          <li key={`processing-${String(methodValue)}`} className={isActive ? "active" : ""}>
                            <span>
                              <i className={`fa-solid ${meta.icon}`} />
                              {label}
                            </span>
                            <strong>{meta.eta}</strong>
                          </li>
                        );
                      })
                    ) : (
                      <li>
                        <span>
                          <i className="fa-solid fa-clock" />
                          {t("cash_in_no_method_selected", { ns: "mobile" })}
                        </span>
                        <strong>-</strong>
                      </li>
                    )}
                  </ul>
                </div>

                <div className="cash-in-help-card">
                  <h3>
                    <i className="fa-solid fa-headset" />
                    {t("contactus_title", { ns: "mobile" })}
                  </h3>
                  <p>
                    {t("cash_in_help_description", { ns: "mobile" })}
                  </p>
                  <button
                    type="button"
                    onClick={() =>
                      openSupportChatOnCurrentPage(
                        navigate,
                        location.pathname,
                        location.search
                      )
                    }
                  >
                    {t("about_live_chat_value", { ns: "mobile" })}
                  </button>
                </div>
              </div>
            </div>
            )}
          </div>
        </div>
      </div>

      <CtsCreationDialog
        open={showCtsDialog}
        onClose={() => setShowCtsDialog(false)}
        onSuccess={handleCtsSuccess}
        initialSourceOfFunds={ctsInitialSourceOfFunds}
      />

      <TransactionPasswordDialog
        open={showPasswordPopup && !!passwordDialogData}
        onClose={handlePasswordDialogClose}
        mode="cashin"
        requestToken={passwordDialogData?.requestToken ?? ""}
        firstMessage={passwordDialogData?.firstMessage ?? ""}
        secondMessage={passwordDialogData?.secondMessage ?? ""}
        openExternalPaymentWindow={isWhishMethod(selectedPaymentMethodData)}
      />
    </div>
  );
}
